/**
 * A numeric diff of two input sets (CLAUDE.md 5.7).
 *
 * Deliberately not welded into the revisions panel. Section 5.7 requires *two*
 * things to show an engineer a before-and-after: restoring a revision, and
 * `POST /positions/{id}/migrate`, which runs a position under a newer module version
 * and "returns a numeric diff of every check" for the engineer to accept or reject.
 * Those are the same question asked twice, so they get one answer.
 *
 * Three rules the presentation follows:
 *
 * - **Values are formatted the way every other number in the product is** (5.4,
 *   ADR-0004): `.` decimal separator in every locale, thin-space grouping, precision
 *   from the quantity kind. A diff that renders `1,234.5` beside a form that renders
 *   `1 234.5` invites the reader to wonder whether they are the same number.
 * - **The kind of change is never colour alone** (14.2): each row carries an icon and
 *   a word as well.
 * - **Unchanged inputs are omitted.** A diff of forty inputs where two moved is a
 *   diff nobody reads.
 */
import { ArrowRight, Minus, PenLine, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
import { formatNumber, formatQuantity } from '@/lib/format'

export type DiffKind = 'added' | 'removed' | 'changed'

export interface DiffEntry {
  key: string
  kind: DiffKind
  before: unknown
  after: unknown
}

/** Structural equality, good enough for the JSON an input model can hold. */
function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (typeof a === 'number' && typeof b === 'number') {
    return Number.isNaN(a) && Number.isNaN(b)
  }
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false
  return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * Compare two input maps.
 *
 * Exported and pure so it can be tested without a DOM, and reused wherever a numeric
 * diff is needed. Keys are sorted so the same pair of inputs always produces the same
 * list -- a diff that reorders itself between renders is a diff the reader re-reads.
 */
export function diffInputs(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): DiffEntry[] {
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort()
  const entries: DiffEntry[] = []

  for (const key of keys) {
    const inBefore = Object.hasOwn(before, key)
    const inAfter = Object.hasOwn(after, key)

    if (inBefore && !inAfter) {
      entries.push({ key, kind: 'removed', before: before[key], after: undefined })
    } else if (!inBefore && inAfter) {
      entries.push({ key, kind: 'added', before: undefined, after: after[key] })
    } else if (!sameValue(before[key], after[key])) {
      entries.push({ key, kind: 'changed', before: before[key], after: after[key] })
    }
  }

  return entries
}

const KIND_STYLE: Record<DiffKind, { icon: typeof Plus; className: string; labelKey: string }> = {
  added: {
    icon: Plus,
    className: 'text-[var(--status-pass)] bg-[var(--status-pass-bg)]',
    labelKey: 'diff.added',
  },
  removed: {
    icon: Minus,
    className: 'text-[var(--status-fail)] bg-[var(--status-fail-bg)]',
    labelKey: 'diff.removed',
  },
  changed: {
    icon: PenLine,
    className: 'text-[var(--status-warn)] bg-[var(--status-warn-bg)]',
    labelKey: 'diff.changed',
  },
}

/**
 * Render one value.
 *
 * Numbers go through the shared formatter, so a value in a diff and the same value in
 * the form are written identically. An exact integer stays exact -- `6`, never
 * `6.000` -- because a span written as a measurement reads as one.
 */
function useValueText() {
  const { t } = useTranslation()

  return (value: unknown): string => {
    if (value === undefined || value === null) return '—'
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) return '—'
      return Number.isInteger(value)
        ? formatNumber(value, { decimals: 0 })
        : formatQuantity(value, 'dimensionless')
    }
    if (typeof value === 'boolean') return value ? t('common.yes') : t('common.no')
    if (typeof value === 'string') return value === '' ? '—' : value
    return JSON.stringify(value)
  }
}

export function InputDiff({
  before,
  after,
  className,
}: {
  before: Record<string, unknown>
  after: Record<string, unknown>
  className?: string
}) {
  const { t } = useTranslation()
  const valueText = useValueText()
  const entries = diffInputs(before, after)

  if (entries.length === 0) {
    return (
      <p className={className} data-testid="input-diff-empty">
        <span className="text-sm text-text-muted">{t('diff.none')}</span>
      </p>
    )
  }

  return (
    <ul className={className} data-testid="input-diff">
      {entries.map((entry) => {
        const style = KIND_STYLE[entry.kind]
        const Icon = style.icon
        return (
          <li
            key={entry.key}
            data-testid={`diff-${entry.kind}`}
            // Stacked below `sm`, a single row above it: a diff on a phone is read,
            // not scanned sideways (14.3).
            className="flex flex-col gap-1 border-b border-border py-2 last:border-b-0 sm:flex-row sm:items-baseline sm:gap-3"
          >
            <span
              className={cn(
                'inline-flex w-fit shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium',
                style.className,
              )}
            >
              <Icon size={12} aria-hidden />
              {t(style.labelKey)}
            </span>

            {/* An input key is an identifier, never translated (14.4). */}
            <span className="min-w-0 flex-1 truncate font-mono text-xs text-text">{entry.key}</span>

            <span className="tabular flex flex-wrap items-baseline gap-1.5 text-sm">
              {entry.kind !== 'added' && (
                <span className={entry.kind === 'changed' ? 'text-text-muted line-through' : ''}>
                  {valueText(entry.before)}
                </span>
              )}
              {entry.kind === 'changed' && (
                <ArrowRight size={12} className="shrink-0 text-text-muted" aria-hidden />
              )}
              {entry.kind !== 'removed' && (
                <span className="font-medium">{valueText(entry.after)}</span>
              )}
            </span>
          </li>
        )
      })}
    </ul>
  )
}
